/**
 * @file `GET/PUT/DELETE /families/{familyId}/study/notes` のクライアント (学びメモ)。
 * @what: ノート (タイトル + Markdown 本文 + 資料への紐づけ refs) の一覧・全体 upsert・削除。
 * @why: 学びメモは記録 (StudyProgressLog) と別の器 (epic_23 §10・§11)。一覧は最近さわった
 *   順で本文込み (メモ帳ペインが選んだ瞬間に本文を出すため)。メモはまなぶ人のもので、
 *   一覧は家族の全件を返すので、学習者で絞るのは client (model/studyNoteContext.ts)。
 */

import { apiFetch } from "@/shared/api/client";
import { invalidateShared } from "../lib/sharedFetchCache";

export interface StudyNoteRef {
  kind: "source";
  source_id: string;
  /** 紙のページ番号。省略 = 資料全体への紐づけ。 */
  page?: number;
}

export interface StudyNoteImage {
  /** 本文からの参照キー (Markdown の `![](note-image:<id>)`)。 */
  id: string;
  /** Storage パス (families/{fid}/study_note_images/{noteId}/...)。 */
  path: string;
}

export interface StudyNote {
  id: string;
  /**
   * このメモのまなぶ人 (family の active member の person id)。学習者を持たずに作った古い
   * メモでは省略される (CLI の assign-notes で割り当てるまでは、どのまなぶ人でも見える)。
   */
  learner_person_id?: string;
  title: string;
  /** Markdown 本文。空のとき省略される。 */
  body?: string;
  /** 紐づけ (省略 = 自由ノート)。 */
  refs?: StudyNoteRef[];
  /** 添えた画像 (省略 = なし)。 */
  images?: StudyNoteImage[];
  /** テーマのタグ (省略 = なし)。別々のメモを1つのテーマに繋ぐ。 */
  tags?: string[];
  created_at: string;
  updated_at: string;
}

export interface StudyNotePutRequest {
  /** このメモのまなぶ人で、必須である (family の active member でなければ404になる)。 */
  learner_person_id: string;
  title: string;
  body: string;
  refs: StudyNoteRef[];
  images: StudyNoteImage[];
  /**
   * テーマのタグ。省略するとサーバは既存のタグを残す (タグを知らない古い画面のため)。
   * この画面はいつも送る。送る前に model/noteTags.ts で正規化する。
   */
  tags?: string[];
}

interface StudyNoteListResponse {
  notes: StudyNote[];
}

function notesPath(familyId: string): string {
  return `/families/${encodeURIComponent(familyId)}/study/notes`;
}

/** 画面をまたいで使い回す一覧の覚え (lib/sharedFetchCache.ts) の key である。書き込みのあとに捨てる。 */
export function studyNotesCacheKey(familyId: string): string {
  return `study-notes:${familyId}`;
}

export async function fetchStudyNotes(
  familyId: string,
  signal?: AbortSignal,
): Promise<StudyNote[]> {
  const res = await apiFetch<StudyNoteListResponse>(notesPath(familyId), signal ? { signal } : {});
  return res.notes;
}

export async function putStudyNote(
  familyId: string,
  noteId: string,
  body: StudyNotePutRequest,
): Promise<StudyNote> {
  const saved = await apiFetch<StudyNote>(`${notesPath(familyId)}/${encodeURIComponent(noteId)}`, {
    method: "PUT",
    body,
  });
  invalidateShared(studyNotesCacheKey(familyId));
  return saved;
}

export async function deleteStudyNote(familyId: string, noteId: string): Promise<void> {
  await apiFetch<undefined>(`${notesPath(familyId)}/${encodeURIComponent(noteId)}`, {
    method: "DELETE",
  });
  invalidateShared(studyNotesCacheKey(familyId));
}

export function newStudyNoteId(): string {
  return `n-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
